import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, LogOut, Users, UserCog, Clock, CheckCircle2, Loader2, Check, X, Dumbbell, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/Logo";
import { AdminAccountForm } from "@/components/AdminAccountForm";
import { ComingSoon } from "@/components/ComingSoon";
import { StatCard } from "@/components/DashboardShell";
import { SettingsForm } from "@/components/SettingsForm";
import { SupportSettingsForm } from "@/components/SupportSettingsForm";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [matches, setMatches] = useState([]);
  const [accountability, setAccountability] = useState(null);
  const [finance, setFinance] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, u, p, m, acc, fin, set] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/partners/pending"),
        api.get("/admin/matches"),
        api.get("/admin/accountability"),
        api.get("/admin/finance"),
        api.get("/admin/settings"),
      ]);
      setStats(s.data);
      setUsers(u.data.users);
      setPending(p.data.partners);
      setMatches(m.data.matches);
      setAccountability(acc.data);
      setFinance(fin.data);
      setSettings(set.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    setActioning(id);
    try {
      await api.post(`/admin/partners/${id}/${action}`);
      toast.success(`Partner ${action === "approve" ? "approved" : "rejected"}.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setActioning(null);
    }
  };

  const handleLogout = async () => { await logout(); navigate("/admin/login"); };

  const roleBadge = (role) => {
    const map = { member: "bg-blue-500/15 text-blue-400", partner: "bg-[#E53E3E]/15 text-[#E53E3E]", admin: "bg-purple-500/15 text-purple-400", super_admin: "bg-amber-500/15 text-amber-400" };
    return map[role] || "bg-zinc-700 text-zinc-300";
  };

  const accountStatus = (u) => {
    if (u.role === "partner") return u.partner_status || "pending";
    if (u.role === "admin" || u.role === "super_admin") return "admin";
    return "active";
  };
  const fmtLocation = (u) => {
    const parts = [u.profile?.city, u.profile?.state].filter(Boolean);
    return parts.length ? parts.join(", ") : "—";
  };
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return "—"; }
  };
  const matchStatusBadge = (s) => {
    const map = { pending: "bg-amber-500/15 text-amber-400", accepted: "bg-green-500/15 text-green-400", declined: "bg-zinc-700 text-zinc-300", ended: "bg-[#E53E3E]/15 text-[#E53E3E]" };
    return map[s] || "bg-zinc-700 text-zinc-300";
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-black/85 backdrop-blur-xl border-b border-zinc-800/80">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo size="h-7" />
            <span className="grid place-items-center w-7 h-7 rounded-lg bg-[#E53E3E]"><ShieldCheck className="w-4 h-4 text-white" /></span>
            <span className="font-display text-base font-extrabold uppercase tracking-tight hidden sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <Badge className={roleBadge(user?.role)} data-testid="admin-role-badge">{user?.role?.replace("_", " ")}</Badge>
            <Button onClick={handleLogout} variant="outline" size="sm" className="border-zinc-700 bg-transparent hover:bg-zinc-900 hover:text-white rounded-full" data-testid="admin-logout-button">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8 md:py-10">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-testid="admin-dashboard-header">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-[#E53E3E]">Platform Overview</span>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-black uppercase tracking-tight">Admin Dashboard</h1>
          </div>
          <Button onClick={load} variant="outline" size="sm" className="border-zinc-700 bg-transparent hover:bg-zinc-900 hover:text-white rounded-full self-start sm:self-auto" data-testid="admin-refresh-button">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="py-20 grid place-items-center"><Loader2 className="w-8 h-8 text-[#E53E3E] animate-spin" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard label="Total Users" value={stats?.total_users ?? 0} />
              <StatCard label="Members" value={stats?.members ?? 0} />
              <StatCard label="Partners" value={stats?.partners ?? 0} hint={`${stats?.approved_partners ?? 0} approved`} />
              <StatCard label="Active Matches" value={stats?.active_matches ?? 0} hint={`${stats?.pending_requests ?? 0} pending requests`} />
            </div>

            <Tabs defaultValue="approvals" className="w-full">
              <TabsList className="bg-zinc-900 border border-zinc-800 h-auto flex-wrap p-1">
                <TabsTrigger value="approvals" className="data-[state=active]:bg-[#E53E3E] data-[state=active]:text-white" data-testid="admin-tab-approvals">
                  Partner Approvals {pending.length > 0 && <span className="ml-2 px-1.5 py-0.5 rounded-full text-[10px] bg-white/20">{pending.length}</span>}
                </TabsTrigger>
                <TabsTrigger value="users" className="data-[state=active]:bg-[#E53E3E] data-[state=active]:text-white" data-testid="admin-tab-users">All Users</TabsTrigger>
                <TabsTrigger value="matches" className="data-[state=active]:bg-[#E53E3E] data-[state=active]:text-white" data-testid="admin-tab-matches">Matches</TabsTrigger>
                <TabsTrigger value="accountability" className="data-[state=active]:bg-[#E53E3E] data-[state=active]:text-white" data-testid="admin-tab-accountability">Accountability</TabsTrigger>
                <TabsTrigger value="finance" className="data-[state=active]:bg-[#E53E3E] data-[state=active]:text-white" data-testid="admin-tab-finance">Finance</TabsTrigger>
                <TabsTrigger value="settings" className="data-[state=active]:bg-[#E53E3E] data-[state=active]:text-white" data-testid="admin-tab-settings">Settings</TabsTrigger>
                <TabsTrigger value="more" className="data-[state=active]:bg-[#E53E3E] data-[state=active]:text-white" data-testid="admin-tab-more">Management</TabsTrigger>
              </TabsList>

              {/* Approvals */}
              <TabsContent value="approvals" className="mt-6">
                {pending.length === 0 ? (
                  <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-12 text-center" data-testid="admin-no-pending">
                    <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto" />
                    <p className="mt-4 text-zinc-400">No partner applications pending review.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pending.map((p) => (
                      <div key={p.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid={`admin-pending-${p.id}`}>
                        <div className="flex items-center gap-4">
                          <span className="grid place-items-center w-11 h-11 rounded-full bg-[#E53E3E]/15 text-[#E53E3E] font-display font-bold">{p.name?.[0]?.toUpperCase()}</span>
                          <div>
                            <p className="font-semibold">{p.name}</p>
                            <p className="text-sm text-zinc-500">{p.email}</p>
                            <div className="text-xs text-zinc-600 mt-1 space-y-0.5">
                              <p>📞 {p.phone || "—"}{p.country ? ` · ${p.country}` : ""}</p>
                              {fmtLocation(p) !== "—" && <p>📍 {fmtLocation(p)}</p>}
                              {p.profile?.sports?.length > 0 && <p>Supports: {p.profile.sports.join(", ")}</p>}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => act(p.id, "approve")} disabled={actioning === p.id} className="bg-green-600 hover:bg-green-700 rounded-full" data-testid={`admin-approve-${p.id}`}>
                            {actioning === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Approve</>}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => act(p.id, "reject")} disabled={actioning === p.id} className="border-zinc-700 bg-transparent hover:bg-[#E53E3E] hover:text-white rounded-full" data-testid={`admin-reject-${p.id}`}>
                            <X className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Users */}
              <TabsContent value="users" className="mt-6">
                <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm whitespace-nowrap">
                      <thead className="bg-zinc-900 text-zinc-500 uppercase text-xs tracking-wider">
                        <tr>
                          <th className="text-left px-5 py-3 font-semibold">Name</th>
                          <th className="text-left px-5 py-3 font-semibold">Role</th>
                          <th className="text-left px-5 py-3 font-semibold">Email</th>
                          <th className="text-left px-5 py-3 font-semibold">Phone / WhatsApp</th>
                          <th className="text-left px-5 py-3 font-semibold">Country</th>
                          <th className="text-left px-5 py-3 font-semibold">State / City</th>
                          <th className="text-left px-5 py-3 font-semibold">Status</th>
                          <th className="text-left px-5 py-3 font-semibold">Registered</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-800" data-testid="admin-users-table">
                        {users.map((u) => (
                          <tr key={u.id} className="hover:bg-zinc-900/60">
                            <td className="px-5 py-3 font-medium">{u.name}</td>
                            <td className="px-5 py-3"><Badge className={roleBadge(u.role)}>{u.role?.replace("_", " ")}</Badge></td>
                            <td className="px-5 py-3 text-zinc-400">{u.email}</td>
                            <td className="px-5 py-3 text-zinc-400">{u.phone || "—"}</td>
                            <td className="px-5 py-3 text-zinc-400">{u.country || "—"}</td>
                            <td className="px-5 py-3 text-zinc-400">{fmtLocation(u)}</td>
                            <td className="px-5 py-3 text-zinc-400 capitalize">{accountStatus(u)}</td>
                            <td className="px-5 py-3 text-zinc-400">{fmtDate(u.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </TabsContent>

              {/* Management placeholders */}
              <TabsContent value="matches" className="mt-6">
                {matches.length === 0 ? (
                  <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-12 text-center" data-testid="admin-no-matches">
                    <p className="text-zinc-400">No match activity yet.</p>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm whitespace-nowrap">
                        <thead className="bg-zinc-900 text-zinc-500 uppercase text-xs tracking-wider">
                          <tr>
                            <th className="text-left px-5 py-3 font-semibold">Member</th>
                            <th className="text-left px-5 py-3 font-semibold">Partner</th>
                            <th className="text-left px-5 py-3 font-semibold">Status</th>
                            <th className="text-left px-5 py-3 font-semibold">Requested</th>
                            <th className="text-left px-5 py-3 font-semibold">Accepted</th>
                            <th className="text-left px-5 py-3 font-semibold">Ended</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-800" data-testid="admin-matches-table">
                          {matches.map((m) => (
                            <tr key={m.id} className="hover:bg-zinc-900/60">
                              <td className="px-5 py-3"><p className="font-medium">{m.member_name}</p><p className="text-xs text-zinc-500">{m.member_email}</p></td>
                              <td className="px-5 py-3"><p className="font-medium">{m.partner_name}</p><p className="text-xs text-zinc-500">{m.partner_email}</p></td>
                              <td className="px-5 py-3"><Badge className={matchStatusBadge(m.status)}>{m.status}</Badge></td>
                              <td className="px-5 py-3 text-zinc-400">{fmtDate(m.created_at)}</td>
                              <td className="px-5 py-3 text-zinc-400">{fmtDate(m.accepted_at)}</td>
                              <td className="px-5 py-3 text-zinc-400">{fmtDate(m.ended_at)}{m.ended_by ? ` (${m.ended_by})` : ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Accountability oversight */}
              <TabsContent value="accountability" className="mt-6 space-y-6" data-testid="admin-accountability">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard label="Active Plans" value={accountability?.stats?.active_plans ?? 0} />
                  <StatCard label="Scheduled" value={accountability?.stats?.scheduled ?? 0} />
                  <StatCard label="Completed" value={accountability?.stats?.completed ?? 0} hint={`${accountability?.stats?.verified_checkins ?? 0} verified`} />
                  <StatCard label="Rejected Check-ins" value={accountability?.stats?.rejected_checkins ?? 0} hint={`${accountability?.stats?.cancelled ?? 0} cancelled`} />
                </div>

                <div>
                  <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-3">Accountability Plans</h3>
                  {(accountability?.plans || []).length === 0 ? (
                    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-8 text-center text-zinc-400">No plans yet.</div>
                  ) : (
                    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm whitespace-nowrap">
                          <thead className="bg-zinc-900 text-zinc-500 uppercase text-xs tracking-wider">
                            <tr><th className="text-left px-5 py-3 font-semibold">Member</th><th className="text-left px-5 py-3 font-semibold">Partner</th><th className="text-left px-5 py-3 font-semibold">Goal</th><th className="text-left px-5 py-3 font-semibold">Activity</th><th className="text-left px-5 py-3 font-semibold">Target</th><th className="text-left px-5 py-3 font-semibold">Status</th></tr>
                          </thead>
                          <tbody className="divide-y divide-zinc-800" data-testid="admin-plans-table">
                            {accountability.plans.map((pl) => (
                              <tr key={pl.id} className="hover:bg-zinc-900/60">
                                <td className="px-5 py-3 font-medium">{pl.member_name}</td>
                                <td className="px-5 py-3 text-zinc-400">{pl.partner_name}</td>
                                <td className="px-5 py-3 text-zinc-400">{pl.goal}</td>
                                <td className="px-5 py-3 text-zinc-400 capitalize">{pl.activity?.replace("-", " ")}</td>
                                <td className="px-5 py-3 text-zinc-400">{pl.target_per_week}×/wk</td>
                                <td className="px-5 py-3"><Badge className={pl.status === "active" ? "bg-green-500/15 text-green-400" : pl.status === "ended" ? "bg-[#E53E3E]/15 text-[#E53E3E]" : "bg-amber-500/15 text-amber-400"}>{pl.status}</Badge></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-3">Check-in History</h3>
                  {(accountability?.checkins || []).length === 0 ? (
                    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-8 text-center text-zinc-400">No check-ins yet.</div>
                  ) : (
                    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm whitespace-nowrap">
                          <thead className="bg-zinc-900 text-zinc-500 uppercase text-xs tracking-wider">
                            <tr><th className="text-left px-5 py-3 font-semibold">Member</th><th className="text-left px-5 py-3 font-semibold">Partner</th><th className="text-left px-5 py-3 font-semibold">Activity</th><th className="text-left px-5 py-3 font-semibold">Date</th><th className="text-left px-5 py-3 font-semibold">Evidence</th><th className="text-left px-5 py-3 font-semibold">Status</th></tr>
                          </thead>
                          <tbody className="divide-y divide-zinc-800" data-testid="admin-checkins-table">
                            {accountability.checkins.map((c) => (
                              <tr key={c.id} className="hover:bg-zinc-900/60">
                                <td className="px-5 py-3 font-medium">{c.member_name}</td>
                                <td className="px-5 py-3 text-zinc-400">{c.partner_name}</td>
                                <td className="px-5 py-3 text-zinc-400 capitalize">{c.activity?.replace("-", " ")}</td>
                                <td className="px-5 py-3 text-zinc-400">{c.date}</td>
                                <td className="px-5 py-3 text-zinc-400">{c.has_evidence ? "Yes (private)" : "—"}</td>
                                <td className="px-5 py-3"><Badge className={matchStatusBadge(c.status === "verified" ? "accepted" : c.status === "rejected" ? "ended" : "pending")}>{c.status}</Badge></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Finance */}
              <TabsContent value="finance" className="mt-6 space-y-6" data-testid="admin-finance">
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                  <StatCard label="Revenue" value={Object.entries(finance?.stats?.revenue || {}).map(([c, a]) => `${finance?.symbols?.[c] || ""}${Number(a).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`).join("  ·  ") || "$0.00"} hint="By currency" />
                  <StatCard label="Active Subs" value={finance?.stats?.active_subs ?? 0} />
                  <StatCard label="Partner Pending" value={Object.entries(finance?.stats?.partner_pending || {}).map(([c, a]) => `${finance?.symbols?.[c] || ""}${Number(a).toFixed(2)}`).join("  ·  ") || "$0.00"} />
                  <StatCard label="Partner Available" value={Object.entries(finance?.stats?.partner_available || {}).map(([c, a]) => `${finance?.symbols?.[c] || ""}${Number(a).toFixed(2)}`).join("  ·  ") || "$0.00"} />
                  <StatCard label="Pending Payouts" value={finance?.stats?.pending_payouts ?? 0} hint={finance?.stats?.reconciliation_flags ? `${finance.stats.reconciliation_flags} to reconcile` : ""} />
                </div>
                {!finance?.paystack_configured && <p className="text-xs text-amber-400">Paystack not configured — revenue shown includes sandbox test activations.</p>}
                {finance?.paystack_configured && (
                  <div className="flex items-center gap-2 text-xs" data-testid="admin-paystack-mode">
                    <span className="text-zinc-500 uppercase tracking-widest font-bold">Paystack mode:</span>
                    <span className={finance?.paystack_mode === "live" ? "px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 font-bold uppercase" : "px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 font-bold uppercase"}>{finance?.paystack_mode === "live" ? "LIVE" : "TEST"}</span>
                  </div>
                )}
                <Button onClick={async () => { try { const { data } = await api.post("/admin/settlement/run"); toast.success(`Settlement: ${data.entries_created} created, ${data.settled_to_available} settled.`); await load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }} className="bg-[#E53E3E] hover:bg-[#C53030] rounded-full" data-testid="admin-run-settlement">Run Period Settlement</Button>
                <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 overflow-hidden">
                  <div className="overflow-x-auto"><table className="w-full text-sm whitespace-nowrap">
                    <thead className="bg-zinc-900 text-zinc-500 uppercase text-xs"><tr><th className="text-left px-5 py-3">Member</th><th className="text-left px-5 py-3">Type</th><th className="text-left px-5 py-3">Amount</th><th className="text-left px-5 py-3">Status</th><th className="text-left px-5 py-3">Provider</th><th className="text-left px-5 py-3">Period</th><th className="text-left px-5 py-3">Action</th></tr></thead>
                    <tbody className="divide-y divide-zinc-800" data-testid="admin-transactions-table">
                      {(finance?.transactions || []).map((t) => (
                        <tr key={t.id}><td className="px-5 py-3">{t.member_name}</td><td className="px-5 py-3 capitalize">{t.type}</td><td className="px-5 py-3">{finance?.symbols?.[t.currency] || "$"}{Number(t.amount).toFixed(2)} <span className="text-zinc-600 text-xs">{t.currency}</span></td><td className="px-5 py-3"><Badge className={t.status === "succeeded" ? "bg-green-500/15 text-green-400" : t.status === "refunded" ? "bg-zinc-700 text-zinc-300" : "bg-[#E53E3E]/15 text-[#E53E3E]"}>{t.status}</Badge></td><td className="px-5 py-3 text-zinc-500 text-xs">{t.provider}</td><td className="px-5 py-3 text-zinc-400">{t.period}</td><td className="px-5 py-3">{t.status === "succeeded" && <button onClick={async () => { try { const { data } = await api.post(`/admin/transactions/${t.id}/refund`); toast.success(`Refunded. ${data.earnings_reversed} earning(s) reversed.${data.flagged ? " Flagged for reconciliation." : ""}`); await load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }} className="text-[#E53E3E] hover:underline text-xs" data-testid={`admin-refund-${t.id}`}>Refund</button>}</td></tr>
                      ))}
                    </tbody></table></div>
                </div>
                <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 overflow-hidden">
                  <div className="overflow-x-auto"><table className="w-full text-sm whitespace-nowrap">
                    <thead className="bg-zinc-900 text-zinc-500 uppercase text-xs"><tr><th className="text-left px-5 py-3">Partner</th><th className="text-left px-5 py-3">Type</th><th className="text-left px-5 py-3">Amount</th><th className="text-left px-5 py-3">Status</th><th className="text-left px-5 py-3">Period</th></tr></thead>
                    <tbody className="divide-y divide-zinc-800" data-testid="admin-ledger-table">
                      {(finance?.ledger || []).map((e) => (
                        <tr key={e.id}><td className="px-5 py-3">{e.partner_name}</td><td className="px-5 py-3 capitalize">{e.type}</td><td className={`px-5 py-3 ${e.amount < 0 ? "text-[#E53E3E]" : "text-green-400"}`}>{finance?.symbols?.[e.currency] || "$"}{Number(e.amount).toFixed(2)} <span className="text-zinc-600 text-xs">{e.currency}</span></td><td className="px-5 py-3"><Badge className="bg-zinc-800 text-zinc-300">{e.status}</Badge></td><td className="px-5 py-3 text-zinc-400">{e.period}</td></tr>
                      ))}
                    </tbody></table></div>
                </div>
              </TabsContent>

              {/* Settings (Super Admin configurable) */}
              <TabsContent value="settings" className="mt-6 space-y-6" data-testid="admin-settings">
                {settings && <SettingsForm settings={settings} isSuper={user?.role === "super_admin"} onSaved={load} />}
                {settings && <SupportSettingsForm settings={settings} isSuper={user?.role === "super_admin"} onSaved={load} />}
              </TabsContent>

              {/* Management + Admin account security */}
              <TabsContent value="more" className="mt-6 space-y-6">
                <AdminAccountForm />
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <ComingSoon title="Matches & Activities" description="Oversee member–partner matches and scheduled activities." />
                  <ComingSoon title="Check-ins & Facilities" description="Manage the facility directory and verify check-ins." />
                  <ComingSoon title="Rewards & Payments" description="Configure rewards, earnings and payment settings." />
                  <ComingSoon title="Reports & Disputes" description="Review flagged content and resolve disputes." />
                  <ComingSoon title="Notifications & Analytics" description="Broadcast notifications and view platform analytics." />
                  <ComingSoon title="Platform Settings" description="Manage sports, staff admins and global settings." />
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>
    </div>
  );
}
